<template>
    <div v-if="activeSegment">
        <grid
            :key="activeSegment.id"
            :columns="columns"
            :filter-set="filterSet"
            :query-component="queryComponent"
            :query="activeSegment.data.filters"
            :provider="provider"
            @sort-change="sortChange"
            @new-segment-save="handleNewSegmentSave"
        >
            <v-no-content slot="no-content" v-if="activeSegment.data.hasCreateCustomerAction">
                <span slot="title">It seems that you have no Customers</span>
                <span
                    slot="description">To start processing payments you must have at least one customer.</span>
                <div slot="actions">
                    <router-link
                        v-if="$auth.$pages['add-customer'].isAllowed"
                        :to="{name: 'add-customer'}"
                        class="inline-s btn btn-primary">Add Customer</router-link>
                </div>
            </v-no-content>
            <v-no-content v-else slot="no-content">
                <span slot="title">It seems that you have no data</span>
                <span slot="description">This segment has no data associated with it, please select another segment.</span>
            </v-no-content>
            <template #left-action>
                <segment-title/>
            </template>
        </grid>
        <api-log-expand-data-model
            :expanded-data="apiLogExpandedData"
            :show="isApiLogModalVisible"
            @close="isApiLogModalVisible = false"
        />
        <webhooks-logs-expand-data-modal
            :expanded-data="webhookLogExpandedData"
            :show="isWebhookLogModalVisible"
            @close="isWebhookLogModalVisible = false"
        />
    </div>
</template>

<script>
    import Grid from '@/components/grid/grid';
    import {vIconButton, vIcon} from '@/components/form';
    import vNoContent from '@/components/v-no-content';
    import gridColumns from '@/modules/segments/data/grid-columns';
    import ColumnSet from '@/components/grid/columns/column-set';
    import SegmentDataModel from '@/common/models/segment-data-model';
    import {mapState, mapGetters, mapActions} from 'vuex';
    import _ from '@/common/helpers';
    import format from '@/common/format';
    import PreferenceTypes from '@/store/preference-types';
    import SegmentTitle from '@/modules/segments/components/segment-title';
    import SegmentFilter from '@/modules/segments/components/segment-filter';
    import ExportDataSegments from '@/modules/segments/mixins/export-data-segments';

    export default {
        mixins: [ExportDataSegments],
        components: {
            Grid,
            vNoContent,
            SegmentTitle,
            vIconButton,
            vIcon,
            ApiLogExpandDataModel: () => import('@/modules/api-keys/components/api-log-expand-data-modal'),
            WebhooksLogsExpandDataModal: () => import('@/modules/webhooks/components/webhooks-tracking-logs-expand-data-modal'),
        },
        computed: {
            /**
             * @property {SegmentModel} activeSegment
             */
            ...mapState('segments', ['activeFilters']),
            ...mapGetters({
                activeSegment: 'segments/activeSegment',
            }),
            columns() {
                if (this.activeSegment && this.activeSegment.data) {
                    // for all the ColumnSets below the `store` property
                    // is being injected through the `gridColumns` object
                    switch (this.activeSegment.data.type) {
                        case SegmentDataModel.Types.apiLogs:
                            return new ColumnSet({
                                id: this.activeSegment.id,
                                ...gridColumns[this.activeSegment.data.type](this.apiLogExpandData),
                                segmentDefaultColumnIds: this.activeSegment.data.columns,
                            });
                        case SegmentDataModel.Types.webhookLogs:
                            return new ColumnSet({
                                id: this.activeSegment.id,
                                ...gridColumns[this.activeSegment.data.type](this.webhookExpandData, this.webhookResend),
                                segmentDefaultColumnIds: this.activeSegment.data.columns,
                            });
                        default:
                            return new ColumnSet({
                                id: this.activeSegment.id,
                                ...gridColumns[this.activeSegment.data.type],
                                segmentDefaultColumnIds: this.activeSegment.data.columns,
                            });
                    }
                }
                return [];
            },
        },
        data() {
            return {
                apiLogExpandedData: {},
                isApiLogModalVisible: false,
                webhookLogExpandedData: {},
                isWebhookLogModalVisible: false,
                queryComponent: SegmentFilter,
                filterSet: null,
            };
        },
        methods: {
            ...mapActions({
                updateSegmentCountFromProvider: 'segments/updateSegmentCountFromProvider',
                updateExportSort: 'segments/updateExportSort',
                addSegment: 'segments/add',
                setPartialUserPreference: 'user/setPartialUserPreference',
                setUserPreference: 'user/setUserPreference',
            }),
            async provider(params) {
                // inject customerId filter when viewing customer details
                // TODO sanity check to verify a customerId filter isn't already present
                const extraFilter = this.activeSegment.data.page === SegmentDataModel.Pages.customerDetails
                    ? `customerId:${this.$store.state.customers.customer.id}`
                    : ``;
                params.filter = params.filter ? `${params.filter};${extraFilter}` : extraFilter;
                let delegate;
                switch (this.activeSegment.data.type) {
                    case SegmentDataModel.Types.couponRedemptions:
                        return this.$api(({coupons}) => coupons.getAllRedemptions(params));
                    case SegmentDataModel.Types.apiLogs:
                        delegate = this.$api(({tracking}) => tracking.getAllApiLogs(params));
                        break;
                    case SegmentDataModel.Types.webhookLogs:
                        delegate = this.$api(({tracking}) => tracking.getAllWebhookTrackingLogs(params));
                        break;
                    default:
                        delegate = this.$api(client => client[this.activeSegment.data.type].getAll(params));
                        break;
                }
                // tap into request to update segment count
                const result = await delegate;
                const {total = null} = result;
                if (total !== null) {
                    // update count in column directly from provider result
                    this.updateSegmentCountFromProvider({segment: this.activeSegment, total});
                }
                return result;
            },
            sortChange(value) {
                this.updateExportSort(value);
            },
            async handleNewSegmentSave(data) {
                try {
                    const created = await this.addSegment(data);
                    this.$toast.positive(`Segment ${data.data.name} was added`);
                    const columns = this.columns.getSelectedColumnIds();
                    this.setPartialUserPreference({
                        type: PreferenceTypes.columns,
                        id: created.id,
                        data: {version: this.columns.version, columns},
                    });
                    const parentSort = this.filterSet.getInitialSort();
                    this.setUserPreference({
                        type: PreferenceTypes.sort,
                        id: created.id,
                        data: parentSort,
                    });
                    this.pushToNewSegment(created);
                } catch (error) {
                    this.$toast.fromError(error);
                }
            },
            pushToNewSegment({id: segmentId, data: {page}}) {
                let to;
                if (page === SegmentDataModel.Pages.lists) {
                    to = {
                        name: SegmentDataModel.Pages.lists,
                        params: {
                            segmentId,
                        },
                    };
                } else {
                    to = {
                        name: `${SegmentDataModel.Pages.customerDetails}-new`,
                        params: {
                            segmentId,
                            id: this.$store.state.customers.customer.id,
                        },
                    };
                }
                this.$router.push(to);
            },
            apiLogExpandData({fields}) {
                this.isApiLogModalVisible = true;
                this.apiLogExpandedData = {
                    response: fields.response,
                    request: fields.request,
                    createdTime: format.dateTime(fields.createdTime),
                    timeAgo: `${_.humanizeDuration(fields.createdTime)} ago`,
                    timeZone: this.getUserPreference(PreferenceTypes.timezone),
                };
            },
            webhookExpandData({fields}) {
                this.isWebhookLogModalVisible = true;
                this.webhookLogExpandedData = {
                    responseBody: fields.responseBody,
                    headers: fields.headers,
                    payload: fields.payload,
                    /* eslint-disable no-underscore-dangle */
                    links: fields._links,
                };
            },
            async webhookResend({fields}, grid) {
                try {
                    const {id} = fields;
                    grid.startLoading();
                    await this.$api(({tracking}) => tracking.resendWebhook({id}));
                    grid.fetchData();
                    this.$toast.positive(`Webhook was resent successfully.`);
                } catch (error) {
                    this.$toast.fromError(error);
                    grid.stopLoading();
                }
            },
        },
    };
</script>
